import { fabric } from 'fabric';

import { CANVAS_OBJECT_TYPE_PREFIX } from '~/core/constants';
import theme from '~/theme/theme';

const {
  drawingShape: { stroke },
} = theme.light.drawingTool;
const hidePrefixes = [CANVAS_OBJECT_TYPE_PREFIX.ARROW_LABEL];
const borderPrefixes = [
  CANVAS_OBJECT_TYPE_PREFIX.SHAPE_EDGE,
  CANVAS_OBJECT_TYPE_PREFIX.BUMP_OUT,
  CANVAS_OBJECT_TYPE_PREFIX.DIAGONAL,
  CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT,
  CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC,
];

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Maps object names to their original stroke colors.
 * Value can be undefined if the object originally had no stroke defined.
 */
type StrokeColorMap = Record<string, string | undefined>;

// ============================================================================
// CANVAS OBJECT PREDICATES
// ============================================================================

/**
 * Checks if a sub-object has a border prefix that should be processed.
 * Returns false for undefined/empty names.
 */
const hasBorderPrefix = (name: string | undefined): boolean =>
  name !== undefined &&
  name !== '' &&
  borderPrefixes.some((prefix) => name.startsWith(prefix));

/**
 * Checks if an object has a prefix that should be hidden during export.
 */
const hasHidePrefix = (name: string | undefined): boolean =>
  hidePrefixes.some((prefix) => name?.startsWith(prefix));

/**
 * Type guard: Checks if an object is a SHAPE_MAIN fabric.Group.
 */
const isShapeMainGroup = (object: fabric.Object): object is fabric.Group =>
  object.name?.startsWith(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN) === true &&
  object instanceof fabric.Group;

// ============================================================================
// BORDER STROKE PROCESSING
// ============================================================================

/**
 * Saves original stroke color and normalizes to gray for export.
 * Mutates both strokeColors map and the fabric object.
 */
const saveBorderStroke = (
  subObject: fabric.Object,
  strokeColors: StrokeColorMap
): void => {
  if (!subObject.name) {
    return;
  }

  strokeColors[subObject.name] = subObject.stroke;
  subObject.set({
    stroke: stroke.normal,
    strokeWidth: 1,
  });
};

/**
 * Restores original stroke color from saved map.
 * Only restores if a truthy stroke value was saved (preserves existing behavior
 * where undefined strokes are not restored).
 */
const restoreBorderStroke = (
  subObject: fabric.Object,
  strokeColors: StrokeColorMap
): void => {
  if (!subObject.name) {
    return;
  }

  const savedStroke = strokeColors[subObject.name];

  if (savedStroke) {
    subObject.set({
      stroke: savedStroke,
      strokeWidth: 2,
    });
  }
};

/**
 * Processes all border sub-objects within a shape group.
 * Either saves and normalizes strokes (remove mode) or restores them (restore mode).
 */
const processBorderSubObjects = (
  group: fabric.Group,
  strokeColors: StrokeColorMap,
  isRemovingColors: boolean
): void => {
  group.getObjects().forEach((subObject) => {
    if (!hasBorderPrefix(subObject.name)) {
      return;
    }

    if (isRemovingColors) {
      saveBorderStroke(subObject, strokeColors);
    } else {
      restoreBorderStroke(subObject, strokeColors);
    }
  });
};

// ============================================================================
// HIDEABLE OBJECT PROCESSING
// ============================================================================

/**
 * Toggles visibility and interactivity of hideable objects (e.g., arrow labels).
 * - Remove mode: Hides object (opacity 0, not selectable/evented)
 * - Restore mode: Shows object (opacity 1, selectable/evented)
 */
const processHideableObject = (
  object: fabric.Object,
  isRemovingColors: boolean
): void => {
  const props = isRemovingColors
    ? { opacity: 0, selectable: false, evented: false }
    : { opacity: 1, selectable: true, evented: true };

  object.set(props);
};

// ============================================================================
// MAIN EXPORTED FUNCTIONS
// ============================================================================

/**
 * Mutates canvas objects by modifying their appearance for image export.
 *
 * Dual-mode operation:
 * - Remove mode (no strokeColors param): Normalizes border strokes to gray,
 *   hides arrow labels, returns map of original stroke colors.
 * - Restore mode (strokeColors param): Restores original border strokes from map,
 *   shows arrow labels, returns undefined.
 *
 * @param canvas - The fabric.js canvas to process
 * @param _strokeColors - Optional map of saved stroke colors for restore mode
 * @returns In remove mode: map of saved stroke colors. In restore mode: undefined.
 */
export const changeObjectStrokeColorsAndPrefixes = (
  canvas: fabric.Canvas,
  _strokeColors?: StrokeColorMap
): StrokeColorMap | undefined => {
  const isRemovingColors = _strokeColors === undefined;
  const strokeColors: StrokeColorMap = _strokeColors ?? {};

  canvas.getObjects().forEach((object) => {
    if (isShapeMainGroup(object)) {
      processBorderSubObjects(object, strokeColors, isRemovingColors);
    }

    if (hasHidePrefix(object.name)) {
      processHideableObject(object, isRemovingColors);
    }
  });

  return isRemovingColors ? strokeColors : undefined;
};

export const changeAndRevertCanvasAppearance = (
  canvas: fabric.Canvas,
  callback: (modifiedCanvas: fabric.Canvas) => void
): void => {
  const originalBackgroundColor = canvas.backgroundColor;
  // BEGIN: change strokes and background (fn below mutates the canvas)
  const originalStrokeColors = changeObjectStrokeColorsAndPrefixes(canvas);

  canvas.setBackgroundColor(theme.light.palette.white, () =>
    canvas.renderAll()
  );
  canvas.renderAll();

  callback(canvas);

  // END: revert strokes and background
  changeObjectStrokeColorsAndPrefixes(canvas, originalStrokeColors);

  if (originalBackgroundColor) {
    canvas.setBackgroundColor(originalBackgroundColor, () =>
      canvas.renderAll()
    );
  }
  canvas.renderAll();
};
